import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import {
  buildChapterExamplesPath,
  buildChapterTopicsPath,
  buildPracticeSectionPath,
  buildPracticeSectionViewPath,
  buildPracticeTopicPath,
  getPracticeSectionForVocabTopic,
  normalizeChapterTrack,
} from '../../utils/contentRoutes';

/** topic → the track's view (dialogue/discussion); vocab/flashcards → their own view. */
const VIEW_BY_KIND = { topic: null, vocab: 'vocab', flashcards: 'flashcards' };

/**
 * The pre-journey catalog URLs (/fr/dialogue/topics/1, /de/discussion/vocab/3, …)
 * are SEO-indexed, so they keep working: we preserve the numeric topic id and
 * forward — in one hop — to the topic-centric home
 * (`/{lang}/fide-practice/topics/{id}/{view}`), or, for a guided A2-section topic
 * (17–19), to that section's own view. The old path is marked noindex with the new
 * one as canonical.
 */
export default function LegacyChapterRedirect({ language = 'fr', track = 'dialogue', kind = 'topic' }) {
  const location = useLocation();
  const { topicId } = useParams();

  const isTopicKind = kind in VIEW_BY_KIND;

  if (!isTopicKind) {
    const target =
      kind === 'examples' && normalizeChapterTrack(track) === 'dialogue'
        ? buildPracticeSectionPath(language, 'a2', 'telephone-roleplay')
        : kind === 'examples'
          ? buildChapterExamplesPath(language, track)
          : buildChapterTopicsPath(language, track);
    return (
      <>
        <Seo path={location.pathname} canonicalUrl={target} noindex />
        <Navigate to={`${target}${location.search}${location.hash}`} replace />
      </>
    );
  }

  const guided = getPracticeSectionForVocabTopic(topicId);
  const view = VIEW_BY_KIND[kind] ?? (normalizeChapterTrack(track) === 'discussion' ? 'discussion' : 'dialogue');
  const target = guided
    ? buildPracticeSectionViewPath(language, guided[0], guided[1], kind === 'topic' ? 'vocab' : view)
    : buildPracticeTopicPath(language, topicId, view);

  return (
    <>
      <Seo path={location.pathname} canonicalUrl={target} noindex />
      <Navigate to={`${target}${location.search}${location.hash}`} replace />
    </>
  );
}
